import { useEffect } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { useQueryClient } from "@tanstack/react-query";
import { AppHeader } from "@/components/app/AppHeader";
import { useAllProfiles, useAllSubmissions } from "@/hooks/use-admin-data";
import { useAuth, DEPARTMENT_LABELS } from "@/lib/auth-context";
import { modCanWorkspace, type ModeratorPermissions } from "@/lib/moderator-permissions";
import { supabase } from "@/integrations/supabase/client";
import { STATUS_LABELS } from "@/lib/submissions";

export const Route = createFileRoute("/_app/_team/team/activity")({
  component: TeamActivityPage,
});

export function TeamActivityPage() {
  const { profile, role } = useAuth();
  const perms = (profile?.moderator_permissions ?? {}) as ModeratorPermissions;
  const canFeed = role === "admin" || modCanWorkspace(perms, "view_activity_feed");

  const qc = useQueryClient();
  const { data: profiles = [] } = useAllProfiles();
  const { data: submissions = [] } = useAllSubmissions();

  // Realtime: invalidate the submissions query whenever any change lands.
  useEffect(() => {
    if (!canFeed) return;
    const ch = supabase
      .channel("team-activity")
      .on("postgres_changes", { event: "*", schema: "public", table: "submissions" }, () => {
        qc.invalidateQueries({ queryKey: ["admin", "submissions"] });
      })
      .subscribe();
    return () => {
      void supabase.removeChannel(ch);
    };
  }, [canFeed, qc]);

  if (!canFeed) {
    return (
      <>
        <AppHeader subtitle="Team — activity feed" />
        <div className="px-[22px] pb-6 text-[12px] text-muted-foreground">
          You don't have permission to view the activity feed.
        </div>
      </>
    );
  }

  const profileById = new Map(profiles.map((p) => [p.id, p]));
  const recent = [...submissions]
    .sort((a, b) => (a.created_at < b.created_at ? 1 : -1))
    .slice(0, 100);

  return (
    <>
      <AppHeader subtitle="Live activity across the workspace" />
      <div className="px-[22px] pb-6">
        <div className="rounded-lg border border-border bg-card shadow-sm">
          <div className="flex items-center justify-between border-b border-border px-4 py-3">
            <h2 className="text-[15px] font-bold">Recent submissions</h2>
            <span className="inline-flex items-center gap-1.5 text-[10px] text-muted-foreground">
              <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-emerald-500" />
              Live
            </span>
          </div>
          <ul className="divide-y divide-border">
            {recent.length === 0 && (
              <li className="px-4 py-6 text-center text-[12px] text-muted-foreground">
                No activity yet.
              </li>
            )}
            {recent.map((s) => {
              const p = profileById.get(s.user_id);
              return (
                <li key={s.id} className="flex items-center justify-between gap-3 px-4 py-2.5">
                  <div className="min-w-0">
                    <div className="truncate text-[12px] font-semibold">{s.title}</div>
                    <div className="text-[10px] text-muted-foreground">
                      {p?.full_name ?? "—"} · {DEPARTMENT_LABELS[s.department]} ·{" "}
                      {format(parseISO(s.created_at), "EEE, dd MMM HH:mm")}
                    </div>
                  </div>
                  <span className="shrink-0 rounded-full bg-muted px-2 py-0.5 text-[10px] capitalize">
                    {STATUS_LABELS[s.status]}
                  </span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </>
  );
}
